/* Apex Wellness — Search Experience Framework v1.0 */

(() => {
  if (window.ApexSearchFrameworkLoaded) return;
  window.ApexSearchFrameworkLoaded = true;

  const SELECTORS = {
    overlay: '[data-apex-search-overlay]',
    open: '[data-apex-search-open]',
    close: '[data-apex-search-close]',
    panel: '[data-apex-search-panel]',
    input: '[data-apex-search-input]',
    result: '[data-apex-search-result]',
    viewAll: '[data-apex-search-view-all]',
    empty: '[data-apex-search-empty]'
  };

  let activeOverlay = null;
  let activeTrigger = null;
  let previouslyFocused = null;

  const getFocusable = (container) =>
    Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hidden && element.offsetParent !== null);

  const setBodyLock = (locked) => {
    document.body.classList.toggle('apex-search-open', locked);
  };

  const openOverlay = (overlay, trigger) => {
    if (!overlay) return;

    previouslyFocused = document.activeElement;
    activeOverlay = overlay;
    activeTrigger = trigger || null;

    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    trigger?.setAttribute('aria-expanded', 'true');
    setBodyLock(true);

    window.requestAnimationFrame(() => {
      const input = overlay.querySelector(SELECTORS.input);
      input?.focus();
      input?.select();
    });

    document.dispatchEvent(
      new CustomEvent('apex:search:open', {
        detail: { overlay }
      })
    );
  };

  const closeOverlay = (overlay = activeOverlay) => {
    if (!overlay) return;

    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    activeTrigger?.setAttribute('aria-expanded', 'false');
    setBodyLock(false);

    const focusTarget = activeTrigger || previouslyFocused;
    if (focusTarget instanceof HTMLElement) {
      window.requestAnimationFrame(() => focusTarget.focus());
    }

    document.dispatchEvent(
      new CustomEvent('apex:search:close', {
        detail: { overlay }
      })
    );

    activeOverlay = null;
    activeTrigger = null;
    previouslyFocused = null;
  };

  const trapFocus = (event) => {
    if (!activeOverlay || event.key !== 'Tab') return;

    const focusable = getFocusable(activeOverlay);
    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const findOverlayForTrigger = (trigger) => {
    const controls = trigger.getAttribute('aria-controls');
    if (controls) return document.getElementById(controls);

    const selector = trigger.dataset.apexSearchTarget;
    if (selector) return document.querySelector(selector);

    return document.querySelector(SELECTORS.overlay);
  };

  const track = (eventName, detail = {}) => {
    document.dispatchEvent(
      new CustomEvent(`apex:search:${eventName}`, {
        detail
      })
    );

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: `apex_search_${eventName}`,
        ...detail
      });
    }
  };

  const handleResultClick = (link) => {
    const resultType = link.dataset.apexSearchResultType || 'unknown';
    const resultPosition = Number(link.dataset.apexSearchResultPosition || 0);
    const input = activeOverlay?.querySelector(SELECTORS.input);
    const query = input?.value.trim() || '';

    track('result_click', {
      query,
      result_type: resultType,
      result_position: resultPosition,
      destination: link.href || ''
    });
  };

  const enhancePredictiveResults = (container) => {
    if (!container || container.dataset.apexSearchEnhanced === 'true') return;
    container.dataset.apexSearchEnhanced = 'true';

    container.addEventListener('click', (event) => {
      const result = event.target.closest(SELECTORS.result);
      if (result) handleResultClick(result);

      const viewAll = event.target.closest(SELECTORS.viewAll);
      if (viewAll) {
        const input =
          activeOverlay?.querySelector(SELECTORS.input) ||
          document.querySelector('predictive-search input[type="search"]');

        track('view_all', {
          query: input?.value.trim() || ''
        });
      }
    });

    const emptyState = container.querySelector(SELECTORS.empty);
    if (emptyState) {
      const input =
        activeOverlay?.querySelector(SELECTORS.input) ||
        document.querySelector('predictive-search input[type="search"]');

      track('empty', {
        query: input?.value.trim() || ''
      });
    }
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;

        if (node.matches?.('[data-apex-predictive-results]')) {
          enhancePredictiveResults(node);
        }

        node
          .querySelectorAll?.('[data-apex-predictive-results]')
          .forEach(enhancePredictiveResults);
      });
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  document.addEventListener('click', (event) => {
    const openTrigger = event.target.closest(SELECTORS.open);
    if (openTrigger) {
      event.preventDefault();
      openOverlay(findOverlayForTrigger(openTrigger), openTrigger);
      return;
    }

    const closeTrigger = event.target.closest(SELECTORS.close);
    if (closeTrigger) {
      event.preventDefault();
      closeOverlay(closeTrigger.closest(SELECTORS.overlay));
      return;
    }

    if (
      activeOverlay &&
      event.target.matches('.apex-search-overlay__backdrop, [data-apex-search-backdrop]')
    ) {
      closeOverlay();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeOverlay) {
      event.preventDefault();
      closeOverlay();
      return;
    }

    trapFocus(event);
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest(SELECTORS.input);
    if (!input) return;

    track('query', {
      query: input.value.trim(),
      length: input.value.trim().length
    });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[role="search"]');
    if (!form) return;

    const input = form.querySelector('input[type="search"]');
    track('submit', {
      query: input?.value.trim() || ''
    });
  });

  document.addEventListener('shopify:section:unload', () => {
    if (activeOverlay) closeOverlay(activeOverlay);
  });

  document
    .querySelectorAll('[data-apex-predictive-results]')
    .forEach(enhancePredictiveResults);
})();
